import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import AppHeader from "../components/AppHeader";
import SortModal, { SORT_COLUMNS } from "../components/SortModal";
import { calcCardValue } from "../utils/cardUtils";
import "./GalleryPage.css";

export default function GalleryPage() {  // NOSONAR: React component complexity is inherent
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const saved = localStorage.getItem("cardstoard_gallery_sort");
      return saved ? JSON.parse(saved) : [{ key: "last_name", direction: "asc" }];
    } catch { return [{ key: "last_name", direction: "asc" }]; }
  });
  const [showSortModal, setShowSortModal] = useState(false);
  const [displayedIdx, setDisplayedIdx] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [fading, setFading] = useState(false);
  const [cardWidth, setCardWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem("cardstoard_gallery_width"), 10);
    return saved >= 200 && saved <= 600 ? saved : 380;
  });
  const fadingRef = useRef(false);
  const displayedIdxRef = useRef(0);
  const navigateToRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const countRes = await api.get("/cards/count");
        const total = countRes.data.count;
        if (total === 0) { setLoading(false); return; }
        const res = await api.get(`/cards/?skip=0&limit=${total}`);
        setCards(res.data);
      } catch (err) {
        console.error("Gallery load error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredCards = useMemo(() => {
    let result = cards.filter(c => c.front_image || c.back_image);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        `${c.first_name} ${c.last_name} ${c.year || ""} ${c.brand || ""}`.toLowerCase().includes(q)
      );
    }
    if (sortConfig.length > 0) {
      const getVal = (card, key) => {
        switch (key) {
          case "year":          return parseInt(card.year, 10) || 0;
          case "last_name":     return card.last_name?.toLowerCase() || "";
          case "first_name":    return card.first_name?.toLowerCase() || "";
          case "grade":         return parseFloat(card.grade) || 0;
          case "card_value":    return Number(card.value) || 0;
          case "market_factor": return Number(card.market_factor) || 0;
          case "rookie":        return Number(card.rookie) || 0;
          default:              return (card[key] || "").toString().toLowerCase();
        }
      };
      result.sort((a, b) => {
        for (const { key, direction } of sortConfig) {
          const aVal = getVal(a, key);
          const bVal = getVal(b, key);
          if (aVal < bVal) return direction === "asc" ? -1 : 1;
          if (aVal > bVal) return direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return result;
  }, [cards, searchQuery, sortConfig]);

  // Reset to first card on filter/sort change
  useEffect(() => {
    setDisplayedIdx(0);
    displayedIdxRef.current = 0;
    setIsFlipped(false);
  }, [searchQuery, sortConfig]);

  const navigateTo = (idx) => {
    if (idx < 0 || idx >= filteredCards.length || fadingRef.current) return;
    fadingRef.current = true;
    setFading(true);
    setTimeout(() => {
      setDisplayedIdx(idx);
      displayedIdxRef.current = idx;
      setIsFlipped(false);
      setFading(false);
      fadingRef.current = false;
    }, 200);
  };

  // Keep navigateToRef current on every render so kiosk listeners always use fresh filteredCards
  useEffect(() => { navigateToRef.current = navigateTo; });

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft")  { e.preventDefault(); navigateTo(displayedIdxRef.current - 1); }
      if (e.key === "ArrowRight") { e.preventDefault(); navigateTo(displayedIdxRef.current + 1); }
      if (e.key === " ") {
        e.preventDefault();
        const card = filteredCards[displayedIdxRef.current];
        if (card?.front_image && card?.back_image) setIsFlipped(f => !f);
      }
      if (e.key === "Enter") {
        const card = filteredCards[displayedIdxRef.current];
        if (card) navigate(`/card-detail/${card.id}`, { state: { cardIds: filteredCards.map(c => c.id) } });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [filteredCards, navigate]);

  // Kiosk event listeners
  useEffect(() => {
    const onNext   = () => navigateToRef.current?.(displayedIdxRef.current + 1);
    const onPrev   = () => navigateToRef.current?.(displayedIdxRef.current - 1);
    const onFlip   = () => setIsFlipped(f => !f);
    const onSearch = (e) => setSearchQuery(e.detail);
    const onZoomIn  = () => setCardWidth(w => Math.min(600, w + 80));
    const onZoomOut = () => setCardWidth(w => Math.max(200, w - 80));
    window.addEventListener("kiosk-gallery-next",     onNext);
    window.addEventListener("kiosk-gallery-prev",     onPrev);
    window.addEventListener("kiosk-gallery-flip",     onFlip);
    window.addEventListener("kiosk-gallery-search",   onSearch);
    window.addEventListener("kiosk-gallery-zoom-in",  onZoomIn);
    window.addEventListener("kiosk-gallery-zoom-out", onZoomOut);
    return () => {
      window.removeEventListener("kiosk-gallery-next",     onNext);
      window.removeEventListener("kiosk-gallery-prev",     onPrev);
      window.removeEventListener("kiosk-gallery-flip",     onFlip);
      window.removeEventListener("kiosk-gallery-search",   onSearch);
      window.removeEventListener("kiosk-gallery-zoom-in",  onZoomIn);
      window.removeEventListener("kiosk-gallery-zoom-out", onZoomOut);
    };
  }, []); // eslint-disable-line

  const handleCardClick = (card) => {
    if (card?.front_image && card?.back_image) setIsFlipped(f => !f);
  };

  const handleCardDoubleClick = (card) => {
    navigate(`/card-detail/${card.id}`, { state: { cardIds: filteredCards.map(c => c.id) } });
  };

  const card = filteredCards[displayedIdx] ?? null;
  const hasBoth = card?.front_image && card?.back_image;

  const selStyle = {
    padding: "0.4rem 0.6rem",
    borderRadius: 6,
    border: "1px solid var(--border)",
    background: "var(--bg-input)",
    color: "var(--text-primary)",
    fontSize: "0.9rem",
  };

  return (
    <>
      <AppHeader />
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "1rem 1rem 2rem" }}>

        {/* Search row */}
        <div style={{ marginBottom: "0.5rem" }}>
          <input
            type="text"
            placeholder="Search by player, year, brand…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-tour="gallery-search"
            data-tip-top="Filter gallery by player name, year, or brand"
            style={{ ...selStyle, width: "100%", boxSizing: "border-box" }}
          />
        </div>

        {/* Sort row — centered below search */}
        <div style={{ textAlign: "center", marginBottom: "0.75rem" }}>
          {(() => {
            let sortLabel = "Sort";
            if (sortConfig.length === 1) {
              const col = SORT_COLUMNS.find(c => c.key === sortConfig[0].key);
              sortLabel = `${col?.label ?? sortConfig[0].key} ${sortConfig[0].direction === "asc" ? "↑" : "↓"}`;
            } else if (sortConfig.length > 1) {
              sortLabel = `${sortConfig.length} sort levels`;
            }
            return (  // NOSONAR
              <button className="nav-btn secondary" onClick={() => setShowSortModal(true)}  // NOSONAR
                title="Sort options" style={{ whiteSpace: "nowrap" }}>
                {sortLabel}
              </button>
            );
          })()}
        </div>

        {/* Zoom + counter row — centered */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>  {/* NOSONAR */}
          <button className="nav-btn secondary" onClick={() => setCardWidth(w => { const n = Math.max(200, w - 40); localStorage.setItem("cardstoard_gallery_width", n); return n; })} style={{ fontSize: "0.8rem", padding: "0.2rem 0.6rem" }} title="Zoom out">🔍−</button>
          <span style={{ fontSize: "0.85rem", color: "var(--text-muted)", minWidth: 80, textAlign: "center" }}>
            {loading ? "Loading…" : filteredCards.length > 0 ? `${displayedIdx + 1} of ${filteredCards.length}` : `${filteredCards.length} cards`}
          </span>
          <button className="nav-btn secondary" onClick={() => setCardWidth(w => { const n = Math.min(600, w + 40); localStorage.setItem("cardstoard_gallery_width", n); return n; })} style={{ fontSize: "0.8rem", padding: "0.2rem 0.6rem" }} title="Zoom in">🔍+</button>
        </div>

        {/* Loading / empty states */}

        {loading && (
          <p style={{ textAlign: "center", color: "var(--text-muted)", marginTop: "3rem" }}>Loading…</p>
        )}
        {!loading && filteredCards.length === 0 && (
          <p style={{ textAlign: "center", color: "var(--text-muted)", marginTop: "3rem" }}>
            {cards.filter(c => c.front_image || c.back_image).length === 0
              ? "No cards with photos yet."
              : "No cards match your search."}
          </p>
        )}

        {/* Card viewer */}
        {!loading && card && (
          <>
            {/* Viewer row: ◀ card ▶ */}
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <button
                className="gallery-nav-btn"
                onClick={() => navigateTo(displayedIdx - 1)}
                disabled={displayedIdx === 0 || fading}
                aria-label="Previous card"
              >◀</button>

              <div className={`gallery-card-wrap${fading ? " fading" : ""}`} style={{ flex: 1 }}>
                <div data-tour="gallery-card" className="gallery-flip-container" style={{ maxWidth: cardWidth }}
                  onClick={() => handleCardClick(card)}
                  onDoubleClick={() => handleCardDoubleClick(card)}
                  data-tip-top={`${card.year} ${card.brand} ${card.first_name} ${card.last_name}${card.card_number ? " #" + card.card_number : ""}${hasBoth ? " · Click to flip · Double-click for detail" : " · Double-click for detail"}`}
                >
                  <div className={`gallery-flip-inner${isFlipped ? " flipped" : ""}`}>
                    {/* Front face */}
                    <div className="gallery-flip-face">
                      <img
                        src={card.front_image || card.back_image}
                        alt="Front"
                        draggable={false}
                      />
                    </div>
                    {/* Back face */}
                    <div className="gallery-flip-face gallery-flip-back-face">
                      {isFlipped && (
                        <span className="gallery-back-badge">Back</span>
                      )}
                      <img
                        src={card.back_image || card.front_image}
                        alt="Back"
                        draggable={false}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button
                className="gallery-nav-btn"
                onClick={() => navigateTo(displayedIdx + 1)}
                disabled={displayedIdx === filteredCards.length - 1 || fading}
                aria-label="Next card"
              >▶</button>
            </div>

            {/* Card info */}
            <div style={{ textAlign: "center", marginTop: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, fontSize: "1.05rem", color: "var(--text-primary)" }}>
                  {card.year} {card.brand} {card.first_name} {card.last_name}
                  {card.rookie ? <span style={{ marginLeft: "0.4rem", color: "gold" }}>⭐</span> : null}
                </span>
                {card.grade && (() => {
                  const { gradeClass } = calcCardValue(card, null);
                  return <span className={`badge badge-grade ${gradeClass}`}>Grade: {card.grade}</span>;
                })()}
              </div>
              {(() => {
                const a = card.card_attributes || {};
                const chips = [
                  a.parallel      && <span key="par" style={{ fontSize: "0.7rem", background: "#7c3aed", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>{a.parallel}</span>,
                  a.refractor     && <span key="ref" style={{ fontSize: "0.7rem", background: "#0ea5e9", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Refractor</span>,
                  a.autograph     && <span key="auto" style={{ fontSize: "0.7rem", background: "#dc2626", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Autograph</span>,
                  a.short_print   && <span key="sp"  style={{ fontSize: "0.7rem", background: "#b45309", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Short Print</span>,
                  a.numbered      && <span key="num" style={{ fontSize: "0.7rem", background: "#374151", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>/{a.numbered}</span>,
                  a.traded        && <span key="tr"  style={{ fontSize: "0.7rem", background: "#065f46", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Traded</span>,
                  a.subset        && <span key="sub" style={{ fontSize: "0.7rem", background: "#6b7280", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>{a.subset}</span>,
                  a.artists_proof && <span key="ap"  style={{ fontSize: "0.7rem", background: "#7e22ce", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Artist's Proof</span>,
                  a.press_proof   && <span key="pp"  style={{ fontSize: "0.7rem", background: "#0f766e", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Press Proof</span>,
                  a.gold          && <span key="gld" style={{ fontSize: "0.7rem", background: "#a16207", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Gold</span>,
                  a.tiffany       && <span key="tif" style={{ fontSize: "0.7rem", background: "#0e7490", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Tiffany</span>,
                  a.update        && <span key="upd" style={{ fontSize: "0.7rem", background: "#4f46e5", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Update</span>,
                  a.error         && <span key="err" style={{ fontSize: "0.7rem", background: "#b91c1c", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>Error</span>,
                  a.nno           && <span key="nno" style={{ fontSize: "0.7rem", background: "#78350f", color: "#fff", borderRadius: 10, padding: "2px 7px" }}>No Number On</span>,
                ].filter(Boolean);
                return chips.length > 0
                  ? <div style={{ display: "flex", gap: "0.4rem", justifyContent: "center", flexWrap: "wrap", marginTop: "0.4rem" }}>{chips}</div>
                  : null;
              })()}
              <button
                data-tour="gallery-detail"
                onClick={() => navigate(`/card-detail/${card.id}`, { state: { cardIds: filteredCards.map(c => c.id) } })}
                className="nav-btn"
                style={{ marginTop: "0.6rem" }}
              >
                → Card Detail
              </button>
            </div>

            {/* Hint */}
            <div style={{ textAlign: "center", marginTop: "1.25rem", fontSize: "0.75rem", color: "var(--text-faint)" }}>
              {hasBoth ? "Click to flip · " : ""}← → to navigate · Space to flip · Enter for detail
            </div>
          </>
        )}
      </div>

      {showSortModal && (
        <SortModal
          sortConfig={sortConfig}
          defaultSort={(() => { try { const s = localStorage.getItem("cardstoard_gallery_sort"); return s ? JSON.parse(s) : []; } catch { return []; } })()}
          onApply={levels => setSortConfig(levels)}
          onClose={() => setShowSortModal(false)}
          showSaveDefault={true}
          onSaveDefault={levels => localStorage.setItem("cardstoard_gallery_sort", JSON.stringify(levels))}
        />
      )}
    </>
  );
}
